/**
 * Registry of the public marketing routes (build F1/F2): one place for each
 * page's title, description, structured data and indexing rules.
 *
 * Read by the pages (`useSEO(seoFor(path))`), the build-time prerender
 * (script/prerender-marketing.ts), the server that bakes the head into the
 * served HTML (server/lib/marketing-html.ts), `/sitemap.xml`, and the SEO
 * tests. Data only: no React, so the server can import it.
 */
import { CORE_PAGES, HOME_FAQ, PROPERTY_MANAGERS_PAGE, RESEARCH_PAGE } from "@/data/core-pages";
import { buildLandingJsonLd, buildPricingJsonLd, faqPage, softwareApplication } from "@/data/marketing-jsonld";
import {
  HOME_SEO,
  PRICING_SEO,
  SAVINGS_CALCULATOR_SEO,
  SIGNUP_SEO,
  SYRE_SEO,
  marketingTitle,
} from "@/data/marketing-seo";

export const SITE_URL = "https://padpulse.io";

type JsonLd = Record<string, unknown>;

export interface MarketingRoute {
  path: string;
  title: string;
  description: string;
  /** Listed in /sitemap.xml and crawlable. */
  indexable: boolean;
  /** Rendered to HTML at build time (otherwise only the head is baked in). */
  prerender: boolean;
  /** Canonical path when it differs from `path` (e.g. /v2 → /). */
  canonicalPath?: string;
  changefreq?: "daily" | "weekly" | "monthly" | "yearly";
  priority?: number;
  jsonLd?: () => JsonLd[];
}

export function breadcrumbs(crumbs: Array<{ name: string; path: string }>): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE_URL}${c.path === "/" ? "" : c.path}` || SITE_URL,
    })),
  };
}

/** Homepage v2 structured data: SoftwareApplication + FAQPage of the A1 FAQ. */
export function buildHomeV2JsonLd(): JsonLd[] {
  return [softwareApplication(), faqPage(HOME_FAQ)];
}

export const HOME_V2_ROUTE_PATH = "/v2";

export const MARKETING_ROUTES: MarketingRoute[] = [
  {
    path: "/",
    title: HOME_SEO.title,
    description: HOME_SEO.description,
    indexable: true,
    prerender: true,
    changefreq: "weekly",
    priority: 1,
    // v1 structured data; the server swaps in buildHomeV2JsonLd() when v2 is live.
    jsonLd: buildLandingJsonLd,
  },
  {
    path: HOME_V2_ROUTE_PATH,
    title: HOME_SEO.title,
    description: HOME_SEO.description,
    indexable: false,
    prerender: true,
    canonicalPath: "/",
    jsonLd: buildHomeV2JsonLd,
  },
  {
    path: "/v3",
    title: marketingTitle("Co-living management · V3 preview"),
    description: "The all-in-one platform built for co-living hosts, on PadSplit or off PadSplit. Explore PadPulse messaging, maintenance and property finances.",
    indexable: false,
    prerender: true,
    canonicalPath: "/",
    jsonLd: buildHomeV2JsonLd,
  },
  {
    path: "/pricing",
    title: PRICING_SEO.title,
    description: PRICING_SEO.description,
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.9,
    jsonLd: () => buildPricingJsonLd(),
  },
  {
    path: "/features",
    title: marketingTitle("All features"),
    description:
      "PadPulse for PadSplit hosts: team inbox, maintenance, locks, leasing, property financials and research. AI-drafted replies require beta access; plans have separate entitlements.",
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.8,
    jsonLd: () => [breadcrumbs([{ name: "Home", path: "/" }, { name: "Features", path: "/features" }])],
  },
  ...CORE_PAGES.map<MarketingRoute>((page) => ({
    path: page.path,
    title: page.metaTitle,
    description: page.metaDescription,
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.8,
    jsonLd: () => [
      breadcrumbs([
        { name: "Home", path: "/" },
        { name: "Features", path: "/features" },
        { name: page.name, path: page.path },
      ]),
    ],
  })),
  {
    path: "/property-managers",
    title: PROPERTY_MANAGERS_PAGE.metaTitle,
    description: PROPERTY_MANAGERS_PAGE.metaDescription,
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.8,
  },
  {
    path: "/research",
    title: RESEARCH_PAGE.metaTitle,
    description: RESEARCH_PAGE.metaDescription,
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.7,
  },
  {
    path: "/savings-calculator",
    title: SAVINGS_CALCULATOR_SEO.title,
    description: SAVINGS_CALCULATOR_SEO.description,
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.6,
  },
  {
    path: "/signup",
    title: SIGNUP_SEO.title,
    description: SIGNUP_SEO.description,
    indexable: false,
    prerender: true,
  },
  {
    path: "/syre",
    title: SYRE_SEO.title,
    description: SYRE_SEO.description,
    indexable: false,
    // Open/closed depends on the clock, so it renders in the browser only.
    prerender: false,
  },
  {
    path: "/blog",
    title: marketingTitle("Blog"),
    description: "Guides and lessons for PadSplit hosts from a top 1% PadSplit host who built PadPulse to run his own houses.",
    indexable: true,
    // Rendered on request from the database (server/lib/marketing-html.ts).
    prerender: false,
    changefreq: "daily",
    priority: 0.8,
  },
  {
    path: "/about",
    title: marketingTitle("About and contact"),
    description:
      "PadPulse is independent software for PadSplit hosts, built by a host. Contact the team at info@padpulse.io.",
    indexable: true,
    prerender: true,
    changefreq: "yearly",
    priority: 0.5,
  },
  {
    path: "/faq/hosts",
    title: marketingTitle("Host FAQ"),
    description: "Answers to common questions from PadSplit hosts using PadPulse.",
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.5,
  },
  {
    path: "/faq/vendors",
    title: marketingTitle("Vendor FAQ"),
    description: "Answers to common questions from vendors working with PadSplit hosts on PadPulse.",
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.4,
  },
  {
    path: "/recommendedfurnishings",
    title: marketingTitle("Recommended Furnishings"),
    description: "The furniture and supplies a top PadSplit host buys for every room and common area.",
    indexable: true,
    prerender: true,
    changefreq: "monthly",
    priority: 0.5,
  },
  {
    path: "/feature-requests",
    title: marketingTitle("Feature requests"),
    description: "Tell the PadPulse team what you want built next.",
    indexable: true,
    prerender: true,
    changefreq: "yearly",
    priority: 0.3,
  },
  {
    path: "/terms",
    title: marketingTitle("Terms of Service"),
    description: "PadPulse Terms of Service, including pricing, trials, refunds and lifetime plans.",
    indexable: true,
    prerender: true,
    changefreq: "yearly",
    priority: 0.3,
  },
  {
    path: "/privacy",
    title: marketingTitle("Privacy Policy"),
    description: "How PadPulse collects, uses and protects your information.",
    indexable: true,
    prerender: true,
    changefreq: "yearly",
    priority: 0.3,
  },
];

const BY_PATH = new Map(MARKETING_ROUTES.map((r) => [r.path, r]));

/** Registry entry for an exact path (trailing slash ignored), or undefined. */
export function marketingRoute(path: string): MarketingRoute | undefined {
  const p = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return BY_PATH.get(p);
}

/** `useSEO` props for a registered path. */
export function seoFor(path: string): {
  title: string;
  description: string;
  path: string;
  canonical?: string;
  jsonLd?: JsonLd[];
  noindex?: boolean;
} {
  const route = marketingRoute(path);
  if (!route) throw new Error(`No marketing route registered for ${path}`);
  return {
    title: route.title,
    description: route.description,
    path: route.path,
    canonical: route.canonicalPath ? `${SITE_URL}${route.canonicalPath === "/" ? "/" : route.canonicalPath}` : undefined,
    jsonLd: route.jsonLd?.(),
    noindex: !route.indexable && route.path !== "/signup" ? true : undefined,
  };
}
